import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, ClipboardList, Maximize2, Printer, Clock, MessageCircle, Copy, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

function generateOrderId() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let id = '';
    for (let i = 0; i < 6; i++) id += chars[Math.floor(Math.random() * chars.length)];
    return id;
}

export default function OrderSummary() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { items, totalPrice, clearCart } = useCart();
    const { rtl } = useLanguage();

    const [orderId] = useState(() => generateOrderId());
    const [tableName, setTableName] = useState('');
    const [carNumber, setCarNumber] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [notes, setNotes] = useState('');
    const [comments, setComments] = useState('');
    const [showStaff, setShowStaff] = useState(false);
    const [settings, setSettings] = useState({ enable_table_number: true, enable_car_number: false, enable_whatsapp_share: true });

    useEffect(() => {
        axios.get(`${API}/menu/${slug}`).then(res => {
            const r = res.data.restaurant;
            setSettings({
                enable_table_number: r.enable_table_number !== false,
                enable_car_number: r.enable_car_number === true,
                enable_whatsapp_share: r.enable_whatsapp_share !== false
            });
        }).catch(() => {});
    }, [slug]);

    if (items.length === 0) {
        navigate(`/menu/${slug}/cart`);
        return null;
    }

    if (showStaff) {
        return (
            <StaffScreen
                orderId={orderId}
                items={items}
                totalPrice={totalPrice}
                tableName={tableName}
                carNumber={carNumber}
                customerName={customerName}
                notes={notes}
                comments={comments}
                onClose={() => setShowStaff(false)}
                onDone={() => { clearCart(); navigate(`/track/${orderId}`); }}
                slug={slug}
                enableWhatsappShare={settings.enable_whatsapp_share}
            />
        );
    }

    const handleShowToStaff = async () => {
        // Send order notification email in background
        try {
            await axios.post(`${API}/menu/${slug}/order`, {
                order_id: orderId,
                items: items.map(i => ({ name: i.name, qty: i.qty, price: i.price })),
                total: totalPrice.toFixed(0),
                table: tableName || undefined,
                car: carNumber || undefined,
                name: customerName || undefined,
                comments: comments || undefined
            });
        } catch {
            // Don't block the flow if email notification fails
        }
        setShowStaff(true);
    };

    return (
        <div className="min-h-screen bg-[#0D0D0D] text-white" dir={rtl ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className="sticky top-0 z-40 backdrop-blur-2xl border-b border-white/5" style={{ backgroundColor: 'rgba(13,13,13,0.92)' }}>
                <div className="max-w-lg mx-auto px-5 py-4 flex items-center gap-3">
                    <button onClick={() => navigate(`/menu/${slug}/cart`)} className="w-10 h-10 rounded-full flex items-center justify-center bg-white/5 active:scale-90 transition-all" data-testid="order-back-btn">
                        <ArrowLeft className="w-[18px] h-[18px] text-white/70" />
                    </button>
                    <div>
                        <h1 className="text-lg font-medium tracking-tight">Order Summary</h1>
                        <p className="text-[11px] text-white/30">Order #{orderId}</p>
                    </div>
                </div>
            </div>

            <div className="max-w-lg mx-auto px-5 py-6 pb-36">
                {/* Order ID */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <div className="px-4 py-3 rounded-xl bg-[#C9A227]/10 border border-[#C9A227]/20 flex items-center gap-3">
                        <ClipboardList className="w-5 h-5 text-[#C9A227]" />
                        <div>
                            <p className="text-xs text-[#C9A227]/60">Order ID</p>
                            <p className="text-lg font-mono font-bold text-[#C9A227] tracking-wider" data-testid="order-id">{orderId}</p>
                        </div>
                    </div>
                </motion.div>

                {/* Optional fields */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-4 mb-8">
                    {settings.enable_table_number && (
                        <div>
                            <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1.5 block">Table Number</label>
                            <Input value={tableName} onChange={e => setTableName(e.target.value)} placeholder="e.g., Table 5" className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 rounded-xl h-12" data-testid="table-input" />
                        </div>
                    )}
                    {settings.enable_car_number && (
                        <div>
                            <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1.5 block">Car Number</label>
                            <Input value={carNumber} onChange={e => setCarNumber(e.target.value)} placeholder="e.g., ABC 1234" className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 rounded-xl h-12" data-testid="car-input" />
                        </div>
                    )}
                    <div>
                        <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1.5 block">Your Name (optional)</label>
                        <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="e.g., Ahmed" className="bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 rounded-xl h-12" data-testid="name-input" />
                    </div>
                    <div>
                        <label className="text-[11px] text-white/30 uppercase tracking-wider mb-1.5 block">Comments / Special Requests</label>
                        <textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Any special requests, dietary needs, allergies..." rows={3} className="w-full bg-white/[0.04] border border-white/[0.08] text-white placeholder:text-white/20 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#C9A227]/40" data-testid="comments-input" />
                    </div>
                </motion.div>

                {/* Items */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-4">
                    <p className="text-[11px] text-white/30 uppercase tracking-wider mb-3">Items</p>
                    <div className="space-y-2">
                        {items.map(item => (
                            <div key={item.id} className="flex items-center justify-between py-3 border-b border-white/[0.04]" data-testid={`order-item-${item.id}`}>
                                <div className="flex items-center gap-3">
                                    <span className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center text-xs font-medium text-white/60">{item.qty}x</span>
                                    <p className="text-[14px]">{item.name}</p>
                                </div>
                                <p className="text-[14px] font-medium text-[#C9A227]">QAR {(item.price * item.qty).toFixed(0)}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Total */}
                <div className="flex items-center justify-between py-4 border-t border-white/[0.08]">
                    <span className="text-white/50">Total</span>
                    <span className="text-xl font-semibold text-[#C9A227]" data-testid="order-total">QAR {totalPrice.toFixed(0)}</span>
                </div>
            </div>

            {/* Sticky Footer */}
            <div className="fixed bottom-0 left-0 right-0 backdrop-blur-2xl border-t border-white/5" style={{ backgroundColor: 'rgba(13,13,13,0.95)' }}>
                <div className="max-w-lg mx-auto px-5 py-5">
                    <Button
                        onClick={handleShowToStaff}
                        className="w-full h-[52px] rounded-2xl text-[15px] font-semibold bg-[#C9A227] text-white hover:bg-[#b8922a] gap-2"
                        style={{ boxShadow: '0 8px 30px rgba(201,162,39,0.25)' }}
                        data-testid="show-to-staff-btn"
                    >
                        <Maximize2 className="w-4 h-4" /> Show to Staff
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ====== Staff Screen ======
function StaffScreen({ orderId, items, totalPrice, tableName, carNumber, customerName, notes, comments, onClose, onDone, slug, enableWhatsappShare = true }) {
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
        } else {
            document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
        }
    };

    const trackingUrl = `${window.location.origin}/track/${orderId}`;
    const qrData = JSON.stringify({
        id: orderId,
        items: items.map(i => ({ n: i.name, q: i.qty, p: i.price })),
        total: totalPrice.toFixed(0),
        table: tableName || undefined,
        car: carNumber || undefined,
        name: customerName || undefined,
        comments: comments || undefined
    });

    return (
        <div className="fixed inset-0 z-[100] bg-[#0D0D0D] text-white overflow-y-auto" data-testid="staff-screen">
            {/* Top bar - Back + Fullscreen only */}
            <div className="sticky top-0 z-10 px-5 py-4 flex items-center justify-between border-b border-white/5 backdrop-blur-2xl" style={{ backgroundColor: 'rgba(13,13,13,0.95)' }}>
                <button onClick={onClose} className="text-[11px] text-white/40 uppercase tracking-wider" data-testid="staff-close-btn">Back</button>
                <button onClick={toggleFullscreen} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center" data-testid="staff-fullscreen-btn">
                    <Maximize2 className="w-4 h-4 text-white/50" />
                </button>
            </div>

            <div className="max-w-md mx-auto px-6 py-8">
                {/* Order ID */}
                <div className="text-center mb-8">
                    <p className="text-[10px] text-white/20 uppercase tracking-[0.25em] mb-2">Order</p>
                    <p className="text-4xl font-mono font-bold text-[#C9A227] tracking-widest" data-testid="staff-order-id">#{orderId}</p>
                </div>

                {/* Meta info */}
                {(tableName || carNumber || customerName) && (
                    <div className="flex justify-center gap-6 mb-8">
                        {tableName && (<div className="text-center"><p className="text-[10px] text-white/20 uppercase tracking-wider mb-1">Table</p><p className="text-2xl font-semibold" data-testid="staff-table">{tableName}</p></div>)}
                        {carNumber && (<div className="text-center"><p className="text-[10px] text-white/20 uppercase tracking-wider mb-1">Car</p><p className="text-2xl font-semibold" data-testid="staff-car">{carNumber}</p></div>)}
                        {customerName && (<div className="text-center"><p className="text-[10px] text-white/20 uppercase tracking-wider mb-1">Name</p><p className="text-2xl font-semibold" data-testid="staff-name">{customerName}</p></div>)}
                    </div>
                )}

                <div className="h-px bg-white/[0.06] mb-6" />

                {/* Items */}
                <div className="space-y-4 mb-6">
                    {items.map(item => (
                        <div key={item.id} className="flex items-center justify-between" data-testid={`staff-item-${item.id}`}>
                            <div className="flex items-baseline gap-3">
                                <span className="text-xl font-bold text-[#C9A227] min-w-[40px]">{item.qty}x</span>
                                <span className="text-xl">{item.name}</span>
                            </div>
                            <span className="text-lg text-white/60 font-medium">QAR {(item.price * item.qty).toFixed(0)}</span>
                        </div>
                    ))}
                </div>

                <div className="border-t border-white/[0.08] pt-5 flex items-center justify-between mb-6">
                    <span className="text-lg text-white/40">Total</span>
                    <span className="text-3xl font-bold text-[#C9A227]" data-testid="staff-total">QAR {totalPrice.toFixed(0)}</span>
                </div>

                {comments && (
                    <div className="mb-4 p-4 rounded-xl bg-[#C9A227]/5 border border-[#C9A227]/10">
                        <p className="text-[10px] text-[#C9A227]/40 uppercase tracking-wider mb-2">Customer Comments</p>
                        <p className="text-sm text-white/80" data-testid="staff-comments">{comments}</p>
                    </div>
                )}

                {notes && (
                    <div className="mb-8 p-4 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Notes</p>
                        <p className="text-sm text-white/70" data-testid="staff-notes">{notes}</p>
                    </div>
                )}

                {/* QR Code - Check Out */}
                <div className="mb-8 flex flex-col items-center" data-testid="staff-qr-section">
                    <p className="text-[10px] text-white/20 uppercase tracking-[0.2em] mb-3">Check Out</p>
                    <div className="p-3 rounded-2xl bg-white">
                        <QRCodeSVG value={qrData} size={140} level="M" fgColor="#0D0D0D" bgColor="#FFFFFF" />
                    </div>
                    <p className="text-[10px] text-white/15 mt-2">Order #{orderId}</p>
                </div>

                {/* Open Tracking Page - Prominent, ABOVE share buttons */}
                <div className="mb-6" data-testid="tracking-page-box">
                    <a
                        href={trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full rounded-2xl overflow-hidden active:scale-[0.98] transition-transform"
                        data-testid="track-order-link"
                    >
                        <div className="px-6 py-5 text-center" style={{ background: 'linear-gradient(135deg, #C9A227 0%, #D4AF37 50%, #C9A227 100%)' }}>
                            <Clock className="w-7 h-7 text-white mx-auto mb-2" />
                            <p className="text-white text-lg font-bold tracking-wide">Open Tracking Page</p>
                            <p className="text-white/70 text-xs mt-1">See your live order status</p>
                        </div>
                    </a>
                </div>

                {/* Share Tracking - WhatsApp/SMS conditional */}
                {enableWhatsappShare && (
                    <div className="mb-4" data-testid="share-tracking-section">
                        <p className="text-[10px] text-white/20 uppercase tracking-[0.2em] mb-3 text-center">Share Tracking Link</p>
                        <div className="flex gap-2 justify-center">
                            <button onClick={() => { window.open(`https://wa.me/?text=${encodeURIComponent(`Your order #${orderId} is being prepared! Track it live: ${trackingUrl}`)}`, '_blank'); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all active:scale-95" style={{ backgroundColor: '#25D366', color: '#fff' }} data-testid="share-whatsapp-btn">
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </button>
                            <button onClick={() => { window.open(`sms:?body=${encodeURIComponent(`Your order #${orderId} is being prepared! Track it live: ${trackingUrl}`)}`, '_blank'); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.08] text-white/70 text-sm font-medium transition-all hover:bg-white/[0.1] active:scale-95" data-testid="share-sms-btn">
                                <Phone className="w-4 h-4" /> SMS
                            </button>
                            <button onClick={() => { navigator.clipboard.writeText(trackingUrl); }} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.06] border border-white/[0.08] text-white/70 text-sm font-medium transition-all hover:bg-white/[0.1] active:scale-95" data-testid="copy-tracking-btn">
                                <Copy className="w-4 h-4" /> Copy Link
                            </button>
                        </div>
                    </div>
                )}

                {/* Action buttons: Back + Done */}
                <div className="flex gap-3">
                    <Button onClick={onClose} variant="outline" className="flex-1 h-12 rounded-xl border-white/10 text-white/60 hover:text-white hover:bg-white/5" data-testid="staff-back-btn">
                        Back
                    </Button>
                    <Button onClick={onDone} className="flex-1 h-12 rounded-xl bg-[#C9A227] text-white hover:bg-[#b8922a]" data-testid="staff-done-btn">
                        Done
                    </Button>
                </div>
            </div>
        </div>
    );
}
